import { useCallback, useState } from 'react'
import { useSelector, useStore } from 'react-redux'

import type { PickerSubmissionPayload } from 'slices/aiConversations/pickerTypes'
import { pickerResponseMessage } from 'slices/aiConversations/thunks'
import {
  PickerSliceState,
  selectGroupDisabled,
} from 'slices/aiSegmentBuilder/picker'
import { buildCanonicalSubmission } from 'slices/aiSegmentBuilder/picker/canonical/canonicalSubmission'

// nexus refuses a longer chat message, and by then the picker shows as submitted
const MAX_MESSAGE_LENGTH = 20_000

type RootState = { picker: PickerSliceState }

/** Exactly what Confirm sends, named after the picker config it answers */
const payloadOf = (
  state: RootState,
  groupId: string,
  pickerType: string
): PickerSubmissionPayload => ({
  ...buildCanonicalSubmission(state, groupId),
  type: pickerType,
})

const isTooLargeToSend = (payload: PickerSubmissionPayload): boolean =>
  pickerResponseMessage(payload).length > MAX_MESSAGE_LENGTH

/** Confirms a canonical picker, unless its response is longer than nexus accepts */
const useCanonicalConfirm = (
  groupId: string,
  pickerType: string,
  onSubmit: (payload: PickerSubmissionPayload) => void
) => {
  const store = useStore<RootState>()
  const [wasRefused, setWasRefused] = useState(false)
  // Follows the picks after a refused Confirm, so removing some clears it
  const isTooLarge = useSelector(
    (state: RootState) =>
      wasRefused && isTooLargeToSend(payloadOf(state, groupId, pickerType))
  )

  const confirm = useCallback(() => {
    const state = store.getState()
    if (selectGroupDisabled(state, groupId)) return

    const payload = payloadOf(state, groupId, pickerType)
    if (isTooLargeToSend(payload)) {
      setWasRefused(true)
      return
    }
    onSubmit(payload)
  }, [groupId, onSubmit, pickerType, store])

  return { confirm, isTooLarge }
}

export default useCanonicalConfirm
